if (!customElements.get('ayo-hero')) {
  class AyoHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ayo-hero-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.querySelectorAll('.ayo-hero__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-ayo-hero-dot]'));
      this.current = 0;

      // which card is "current": the one whose start edge is nearest the track start
      // (or the last one when the track is scrolled to its end)
      this.onScroll = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          if (this.lockUntil && Date.now() < this.lockUntil) return;
          this.setCurrent(this.nearest());
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      const unlock = () => { this.lockUntil = 0; };
      this.track.addEventListener('pointerdown', unlock);
      this.track.addEventListener('touchstart', unlock, { passive: true });
      this.track.addEventListener('wheel', unlock, { passive: true });
      this.setCurrent(0);

      this.dots.forEach((d) => d.addEventListener('click', () => this.go(parseInt(d.dataset.ayoHeroDot, 10))));
      const prev = this.querySelector('[data-ayo-hero-prev]');
      const next = this.querySelector('[data-ayo-hero-next]');
      if (prev) prev.addEventListener('click', () => this.go(this.current - 1));
      if (next) next.addEventListener('click', () => this.go(this.current + 1));
      this.track.addEventListener('keydown', (e) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.current + (rtl ? -1 : 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.current + (rtl ? 1 : -1)); }
      });

      // autoplay: pauses while touching, hovering, hidden, or when the user prefers less motion
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      // a finger on the cards pauses, and play picks up again a little after the finger lifts
      const pause = () => { this.paused = true; clearTimeout(this.resumeTimer); };
      const resumeLater = () => { clearTimeout(this.resumeTimer); this.resumeTimer = setTimeout(() => { this.paused = false; }, this.interval * 1.5); };
      this.track.addEventListener('touchstart', pause, { passive: true });
      this.track.addEventListener('touchend', resumeLater, { passive: true });
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') pause(); });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { clearTimeout(this.resumeTimer); this.paused = false; } });
      this.addEventListener('focusin', (e) => { let kb = true; try { kb = e.target.matches(':focus-visible'); } catch (err) { kb = true; } if (kb) pause(); });
      this.addEventListener('focusout', () => { this.paused = false; });
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if ('IntersectionObserver' in window) {
        this.viewObs = new IntersectionObserver((en) => { this.offscreen = !en[0].isIntersecting; });
        this.viewObs.observe(this);
      }
      if (this.autoplay) this.timer = setInterval(() => {
        if (this.paused || this.offscreen || document.hidden || this.inEditor) return;
        this.go((this.current + 1) % this.slides.length);
      }, this.interval);

      // theme editor: jump to the selected slide and stop autoplay while editing it
      document.addEventListener('shopify:block:select', (e) => {
        const sl = e.target.closest && e.target.closest('.ayo-hero__slide');
        if (sl && this.contains(sl)) { this.inEditor = true; this.go(parseInt(sl.dataset.index, 10), true); }
      });
      document.addEventListener('shopify:block:deselect', () => { this.inEditor = false; });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      clearTimeout(this.resumeTimer);
      if (this.viewObs) this.viewObs.disconnect();
    }

    edges() {
      const tr = this.track.getBoundingClientRect();
      const cs = getComputedStyle(this.track);
      const pad = parseFloat(cs.scrollPaddingInlineStart) || 0;
      const rtl = cs.direction === 'rtl';
      return { rtl, start: rtl ? tr.right - pad : tr.left + pad };
    }

    edgeOf(sl, rtl) {
      const r = sl.getBoundingClientRect();
      return rtl ? r.right : r.left;
    }

    nearest() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (max > 0 && Math.abs(t.scrollLeft) >= max - 2) return this.slides.length - 1;
      const { rtl, start } = this.edges();
      let best = 0; let dist = Infinity;
      this.slides.forEach((sl, i) => {
        const d = Math.abs(this.edgeOf(sl, rtl) - start);
        if (d < dist) { dist = d; best = i; }
      });
      return best;
    }

    go(i, instant) {
      if (!this.slides.length) return;
      const n = this.slides.length;
      const idx = ((i % n) + n) % n;
      const target = this.slides[idx];
      const { rtl, start } = this.edges();
      const delta = this.edgeOf(target, rtl) - start;
      this.lockUntil = Date.now() + (instant ? 150 : 900);
      this.track.scrollBy({ left: delta, behavior: instant || this.reduced ? 'auto' : 'smooth' });
      this.setCurrent(idx);
    }

    setCurrent(idx) {
      if (Number.isNaN(idx)) return;
      this.current = idx;
      this.slides.forEach((sl, i) => sl.classList.toggle('is-current', i === idx));
      this.dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-current', i === idx ? 'true' : 'false');
      });
    }
  }
  customElements.define('ayo-hero', AyoHero);
}
